import React from "react";

/**
 * MarkdownBody — the shared editorial renderer used by /stories/{id} and by
 * the Editorial live-preview pane. Supports a small superset of Markdown:
 *
 *   ## / ### / ####     — headings
 *   - foo / * foo       — bullet lists
 *   **bold**            — inline bold
 *   [text](url)         — inline link
 *   ![alt](url)         — centered figure (default)
 *   ![alt|left](url)    — floated left, text wraps on right
 *   ![alt|right](url)   — floated right, text wraps on left
 *   ![alt|center](url)  — explicit centered (same as no suffix)
 *
 *   N consecutive image lines (no blank line between them) render as a
 *   responsive gallery grid instead of stacked figures.
 *
 *   A whole line that is just a URL becomes:
 *     - YouTube / Vimeo / Loom → a lite click-to-load 16:9 embed
 *     - Anything else → an autolinked <a>
 */

// ---------- URL → embed metadata ----------
export function embedUrlFor(raw) {
  if (!raw) return null;
  let url;
  try { url = new URL(raw.trim()); } catch { return null; }
  const host = url.hostname.replace(/^www\./, "").toLowerCase();
  if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
    const v = url.searchParams.get("v");
    if (v) return { kind: "youtube", id: v };
    const m = url.pathname.match(/^\/(shorts|embed|live)\/([\w-]+)/);
    if (m) return { kind: "youtube", id: m[2] };
  }
  if (host === "youtu.be") {
    const id = url.pathname.replace(/^\//, "").split("/")[0];
    if (id) return { kind: "youtube", id };
  }
  if (host.endsWith("vimeo.com")) {
    const id = url.pathname.split("/").filter(Boolean).pop();
    if (id && /^\d+$/.test(id)) return { kind: "vimeo", id };
  }
  if (host === "loom.com" || host === "www.loom.com") {
    const m = url.pathname.match(/\/share\/([a-f0-9]{20,})/i);
    if (m) return { kind: "loom", id: m[1] };
  }
  return null;
}

// Click-to-load lite embed — avoids the black flash while YouTube boots.
function LiteEmbed({ meta, keyId }) {
  const [loaded, setLoaded] = React.useState(false);
  let iframeSrc = "";
  let poster = "";
  let host = "";
  if (meta.kind === "youtube") {
    iframeSrc = `https://www.youtube-nocookie.com/embed/${meta.id}?autoplay=1&rel=0`;
    poster = `https://i.ytimg.com/vi/${meta.id}/hqdefault.jpg`;
    host = "YouTube";
  } else if (meta.kind === "vimeo") {
    iframeSrc = `https://player.vimeo.com/video/${meta.id}?autoplay=1`;
    host = "Vimeo";
  } else if (meta.kind === "loom") {
    iframeSrc = `https://www.loom.com/embed/${meta.id}?autoplay=1`;
    host = "Loom";
  }
  if (loaded) {
    return (
      <div key={keyId} className="my-6 clear-both rounded-2xl overflow-hidden border border-black/10 bg-black aspect-video">
        <iframe
          src={iframeSrc}
          title={`${host} embed`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="w-full h-full"
        />
      </div>
    );
  }
  return (
    <button
      key={keyId}
      type="button"
      onClick={() => setLoaded(true)}
      className="my-6 clear-both group relative w-full aspect-video rounded-2xl overflow-hidden border border-black/10 bg-black text-left"
      aria-label={`Play ${host} video`}
    >
      {poster && (
        <img
          src={poster}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
      <div className="absolute top-3 left-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-white/90 bg-black/45 backdrop-blur-sm px-2 py-1 rounded-full">
        ▶ Play {host}
      </div>
      <div className="absolute inset-0 grid place-items-center">
        <span className="grid place-items-center w-16 h-16 rounded-full bg-white/90 text-[#B85F2A] shadow-lg transition-transform group-hover:scale-110">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7L8 5z" />
          </svg>
        </span>
      </div>
    </button>
  );
}

// ---------- Inline: **bold**, [text](url) ----------
function inline(text) {
  const parts = [];
  const combined = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*(.+?)\*\*/g;
  let last = 0, m, key = 0;
  while ((m = combined.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[3] !== undefined) {
      parts.push(<strong key={`b${key++}`}>{m[3]}</strong>);
    } else {
      parts.push(
        <a key={`a${key++}`} href={m[2]} target="_blank" rel="noopener noreferrer" className="underline text-[color:var(--np-brand,#B85F2A)] hover:text-[#8a4820]">
          {m[1]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Extract alignment (left/center/right) from an "alt|tag" alt-text string.
function splitAlignedAlt(raw) {
  const rawAlt = raw || "";
  const pipe = rawAlt.lastIndexOf("|");
  if (pipe < 0) return { alt: rawAlt, align: "center" };
  const tag = rawAlt.slice(pipe + 1).trim().toLowerCase();
  if (tag === "left" || tag === "right" || tag === "center") {
    return { alt: rawAlt.slice(0, pipe).trim(), align: tag };
  }
  return { alt: rawAlt, align: "center" };
}

// Render a single figure — respects alignment for solo (non-gallery) images.
function Figure({ url, alt, align, keyId }) {
  const wrapper =
    align === "left"  ? "float-left mr-5 mb-3 w-full sm:w-1/2 max-w-[420px] clear-none" :
    align === "right" ? "float-right ml-5 mb-3 w-full sm:w-1/2 max-w-[420px] clear-none" :
                        "mx-auto clear-both w-full max-w-full";
  const imgCls =
    align === "center"
      ? "rounded-2xl w-full max-h-[560px] object-cover border border-black/10"
      : "rounded-2xl w-full h-auto object-cover border border-black/10";
  return (
    <figure key={keyId} className={`my-4 ${wrapper}`}>
      <img src={url} alt={alt} loading="lazy" className={imgCls} />
      {alt && (
        <figcaption className={`mt-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] ${align === "center" ? "text-center" : ""}`}>
          {alt}
        </figcaption>
      )}
    </figure>
  );
}

// Responsive gallery grid — 2 cols on mobile, 3 on tablet+.
function Gallery({ items, keyId }) {
  const cols = items.length === 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3";
  return (
    <div key={keyId} className={`my-6 clear-both grid ${cols} gap-2`}>
      {items.map((it, i) => (
        <figure key={i} className="relative aspect-[4/3] overflow-hidden rounded-xl border border-black/10 bg-black/5">
          <img src={it.url} alt={it.alt || ""} loading="lazy" className="w-full h-full object-cover" />
          {it.alt && (
            <figcaption className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/60 to-transparent font-mono-tech text-[9px] tracking-[0.22em] uppercase text-white truncate">
              {it.alt}
            </figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}

// ---------- Main renderer ----------
export default function MarkdownBody({ text }) {
  const lines = (text || "").split(/\r?\n/);
  const blocks = [];
  let listBuf = [];
  let imgBuf = []; // consecutive image lines → collapse into a gallery when >=2
  let key = 0;

  const flushList = () => {
    if (listBuf.length) {
      blocks.push(
        <ul key={`ul${key++}`} className="list-disc pl-6 my-3 space-y-1 text-[16px] leading-[1.65]">
          {listBuf.map((li, i) => <li key={i}>{inline(li)}</li>)}
        </ul>
      );
      listBuf = [];
    }
  };
  const flushImgs = () => {
    if (!imgBuf.length) return;
    if (imgBuf.length === 1) {
      const it = imgBuf[0];
      blocks.push(<Figure key={`fig${key++}`} keyId={key} url={it.url} alt={it.alt} align={it.align} />);
    } else {
      // Any image with left/right alignment breaks the gallery — render each solo.
      const anyFloat = imgBuf.some((i) => i.align !== "center");
      if (anyFloat) {
        imgBuf.forEach((it) => blocks.push(<Figure key={`fig${key++}`} keyId={key} url={it.url} alt={it.alt} align={it.align} />));
      } else {
        blocks.push(<Gallery key={`gal${key++}`} keyId={key} items={imgBuf} />);
      }
    }
    imgBuf = [];
  };

  const IMG_RE = /^!\[([^\]]*)\]\(([^)\s]+)\)$/;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    // Blank line → flush both list and image buffers.
    if (!line) { flushList(); flushImgs(); continue; }
    // Image line — accumulate.
    const im = IMG_RE.exec(line);
    if (im) {
      flushList();
      const { alt, align } = splitAlignedAlt(im[1]);
      imgBuf.push({ url: im[2], alt, align });
      continue;
    }
    // Anything else → flush image buffer first.
    if (imgBuf.length) flushImgs();
    // Standalone URL → embed or plain link.
    if (/^https?:\/\/\S+$/.test(line)) {
      const embed = embedUrlFor(line);
      if (embed) {
        flushList();
        blocks.push(<LiteEmbed key={`emb${key++}`} keyId={key} meta={embed} />);
        continue;
      }
      // Plain autolink
      flushList();
      blocks.push(
        <p key={`al${key++}`} className="my-3 text-[17px] leading-[1.75] text-[#241209]">
          <a href={line} target="_blank" rel="noopener noreferrer" className="underline text-[color:var(--np-brand,#B85F2A)] hover:text-[#8a4820] break-all">
            {line}
          </a>
        </p>
      );
      continue;
    }
    if (line.startsWith("#### ")) { flushList(); blocks.push(<h4 key={i} className="font-serif-display text-lg mt-4 mb-1">{inline(line.slice(5))}</h4>); continue; }
    if (line.startsWith("### ")) { flushList(); blocks.push(<h3 key={i} className="font-serif-display text-xl mt-5 mb-1.5">{inline(line.slice(4))}</h3>); continue; }
    if (line.startsWith("## ")) { flushList(); blocks.push(<h2 key={i} className="font-serif-display text-2xl mt-6 mb-2 text-[color:var(--np-brand,#B85F2A)]">{inline(line.slice(3))}</h2>); continue; }
    if (line.startsWith("# ")) { flushList(); blocks.push(<h2 key={i} className="font-serif-display text-2xl mt-6 mb-2 text-[color:var(--np-brand,#B85F2A)]">{inline(line.slice(2))}</h2>); continue; }
    if (line.startsWith("- ") || line.startsWith("* ")) { listBuf.push(line.slice(2)); continue; }
    flushList();
    blocks.push(<p key={i} className="my-3 text-[17px] leading-[1.75] text-[#241209]">{inline(line)}</p>);
  }
  // Final flush.
  flushList();
  flushImgs();
  return <div className="max-w-none font-serif-display text-[#241209]">{blocks}</div>;
}
